import type { Interactable } from "@interactjs/core/Interactable"; // Rename imported type to avoid conflict with the context constant
import interact from "interactjs";
import { isNil, propEq } from "ramda";
import { isNotNil, isNull } from "ramda-adjunct";
import React, {
  createContext,
  FC,
  MutableRefObject,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
  useTransition,
} from "react";
import { useRateTreeContext } from "@sinch/components/application/RatelistBuilder/BuilderTree/RateBuilderTreeProvider";

import { RateBuilderTreeViewItem } from "@sinch/components/application/RatelistBuilder/types";

// Rename imported type to avoid conflict with the context constant

type TreeDnDState = MutableRefObject<string | null>;
// The context constant remains named TreeDnD
export const TreeDnD = createContext<TreeDnDState>({ current: null });

export const useTreeDnD = ({
  treeItemId,
  dropzoneItemId,
  index,
}: {
  treeItemId?: string;
  dropzoneItemId?: string | null;
  index?: number;
}) => {
  const [dragged, setDragged] = useState(false);
  const [isPending, startTransition] = useTransition();
  const draggedItemId = useContext(TreeDnD);
  const { move, getParent, get, tree } = useRateTreeContext<RateBuilderTreeViewItem>();

  const posRef = useRef({ x: 0, y: 0 });

  const dragInstanceRef = useRef<Interactable | null>(null);

  const draggedHandlerRef = useRef<HTMLDivElement>(null);
  const draggedElRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (draggedHandlerRef.current) {
      dragInstanceRef.current = interact(draggedHandlerRef.current);
      dragInstanceRef.current.draggable({
        hold: 100,
        enabled: isNotNil(treeItemId),
        autoScroll: true /*{
          //distance?: number;
          //interval?: number;
          speed: 300,
          enabled: true,
        }*/,
        // dragMoveListener from the dragging demo above
        listeners: {
          start: (event) => {
            if (draggedElRef.current) {
              draggedItemId.current = treeItemId ?? null;
              const top = event.page.y - event.rect.top;
              draggedElRef.current.style.zIndex = "10000";
              draggedElRef.current.style.transform = `translate(0px, ${top + 10}px)`;
              draggedElRef.current.classList.add("dragged");
              posRef.current.x = 0;
              posRef.current.y = 0;
              setDragged(true);
            }
          },
          move: (event) => {
            posRef.current.x += event.dx;
            posRef.current.y += event.dy;

            if (draggedElRef.current) {
              const top = event.page.y - event.rect.top;
              draggedElRef.current.style.transform = `translate(${posRef.current.x}px, ${posRef.current.y + top + 10}px)`;
            }
          },
          end: () => {
            if (draggedElRef.current) {
              draggedElRef.current.style.transform = "";
              draggedElRef.current.style.zIndex = "auto";
              draggedElRef.current.classList.remove("dragged");
              posRef.current.x = 0;
              posRef.current.y = 0;
              setDragged(false);
            }
          },
        },
      });
    }
    return () => {
      if (draggedHandlerRef.current) {
        dragInstanceRef.current?.unset();
        dragInstanceRef.current = null;
      }
    };
  }, [draggedItemId, treeItemId]);

  useEffect(() => {
    if (!treeItemId) {
      dragInstanceRef.current?.draggable(isNotNil(treeItemId));
    }
  }, [treeItemId]);

  const dropOverlayRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (dropOverlayRef.current) {
      interact(dropOverlayRef.current).dropzone({
        // only accept elements matching this CSS selector
        //accept: "#yes-drop",
        // Require a 75% element overlap for a drop to be possible
        //overlap: 0.75,
        accept: () => {
          if (isNil(draggedItemId.current)) {
            return false;
          }
          const oldParent = getParent(draggedItemId.current);
          if (oldParent?.id && oldParent?.type === "wrapper") {
            return oldParent.id === draggedItemId.current;
          }

          const oldIndex = (oldParent === null ? tree : oldParent?.children)?.findIndex(
            propEq("id", draggedItemId.current)
          );

          if (
            index &&
            (oldIndex === index || oldIndex === index - 1) &&
            (oldParent?.id ?? null) === (dropzoneItemId ? getParent(dropzoneItemId)?.id : null)
          ) {
            return false;
          }

          if (dropzoneItemId) {
            let parent = get(dropzoneItemId);
            do {
              if (parent?.id === draggedItemId.current) {
                return false;
              }
            } while (parent && (parent = getParent(parent.id)));
          }

          if (isNull(dropzoneItemId)) {
            return true;
          }
          const dropzoneItem = get(dropzoneItemId!);
          return dropzoneItem?.type !== "wrapper";
        },
        // listen for drop related events:

        ondropactivate: function (event) {
          // add active dropzone feedback
          event.target.style.display = "block";
        },
        ondragenter: function (event) {
          const dropzoneElement = event.target;

          // feedback the possibility of a drop
          dropzoneElement.style.visibility = "visible";
        },
        ondragleave: function (event) {
          // remove the drop feedback style
          event.target.style.visibility = "hidden";
        },
        ondrop: function () {
          if (isNotNil(draggedItemId.current)) {
            startTransition(() => {
              move(draggedItemId.current!, dropzoneItemId ?? null, index);
            });
          }
          draggedItemId.current = null;
        },
        ondropdeactivate: function (event) {
          // remove active dropzone feedback
          event.target.style.display = "none";
        },
      });
    }
    return () => {
      if (dropOverlayRef.current) {
        interact(dropOverlayRef.current).unset();
      }
    };
  }, [draggedItemId, dropzoneItemId, index, move, tree]);

  return { dragged, draggedHandlerRef, draggedElRef, dropOverlayRef, loading: isPending };
};

export const TreeDnDProvider: FC<PropsWithChildren> = ({ children }) => {
  const draggedItem = useRef<string | null>(null);
  return <TreeDnD.Provider value={draggedItem}>{children}</TreeDnD.Provider>;
};
